import styles from './posts.module.css';
import {
  FaEllipsisV, FaTag, FaExclamationTriangle, FaCalendar,
  FaBriefcase, FaNewspaper, FaLightbulb, FaMapMarkerAlt, FaUsers,
  FaClock, FaCheckCircle, FaPlusCircle, FaHeart, FaRegHeart,
  FaRegCommentDots, FaPaperPlane, FaRegBookmark, FaBookmark,
  FaLink, FaUserPlus, FaVolumeMute, FaFlag, FaTrash, FaEyeSlash,
  FaEdit, FaUserTimes, FaQuestionCircle, FaMotorcycle, FaCar
} from "react-icons/fa";
import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Friend } from './Friend';
import { useNavigate } from 'react-router-dom';
import Toast from './Toast';
import { getNotif } from '../Utils/GetNotif';
import { API_URL } from '../Utils/api';
import { usePosts } from '../hooks/UsePosts';
import CloudinaryImage from '../Utils/CloudinaryImage';

/* ─── Config ──────────────────────────────────────────────────── */
const TYPE_CONFIG = {
  announcement: { icon: FaCalendar,           color: '#1d9bf0', label: 'Annonce'     },
  alert:        { icon: FaExclamationTriangle, color: '#ff6b35', label: 'Alerte'      },
  event:        { icon: FaCalendar,            color: '#00ba7c', label: 'Événement'   },
  opportunity:  { icon: FaBriefcase,           color: '#1d9bf0', label: 'Opportunité' },
  news:         { icon: FaNewspaper,           color: '#f59e0b', label: 'Actualité'   },
  tip:          { icon: FaLightbulb,           color: '#06b6d4', label: 'Astuce'      },
  sale:         { icon: FaTag,                 color: '#10b981', label: 'Vendre'      },
};

const PRIORITY_CONFIG = {
  low:    { color: '#71767b', label: 'Basse'   },
  normal: { color: '#1d9bf0', label: 'Normale' },
  high:   { color: '#f59e0b', label: 'Haute'   },
  urgent: { color: '#ff6b35', label: 'Urgente' },
};
const CATEGORY_COLORS = {
  academic: '#1d9bf0', technical: '#1d9bf0', career: '#00ba7c',
  life: '#f59e0b', other: '#71767b', sport: '#00ba7c',
  culture: '#1d9bf0', education: '#1d9bf0', social: '#f59e0b', technology: '#06b6d4',
};

/* ─── Helpers ─────────────────────────────────────────────────── */
const manageTime = (postDate) => {
  if (!postDate) return '';
  const diff = Date.now() - new Date(postDate);
  const s = Math.floor(diff / 1000), m = Math.floor(diff / 60000),
        h = Math.floor(diff / 3600000), d = Math.floor(diff / 86400000);
  if (s < 60) return "maintenant";
  if (m < 60) return `${m}min`;
  if (h < 24) return `${h}h`;
  if (d < 7)  return `${d}j`;
  if (d < 30) return `${Math.floor(d / 7)} sem`;
  const mo = Math.floor(d / 30);
  return mo < 12 ? `${mo} mois` : `${Math.floor(d / 365)} an${Math.floor(d / 365) > 1 ? 's' : ''}`;
};

const fmtEvent = (d) => new Date(d).toLocaleDateString('fr-FR', {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
});

/* ══════════════════════════════════════════════════════════
   POST OPTIONS MODAL (3 points)
══════════════════════════════════════════════════════════ */
const PostOptionsModal = ({ post, userId, onClose, onDelete, onCopyLink, onReport, anchorRef }) => {
  const isOwner = post.userId === userId || post.userId?.toString() === userId?.toString();
  const modalRef = useRef(null);

  /* Ferme au clic extérieur */
  useEffect(() => {
    const handle = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target) &&
          anchorRef?.current && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [onClose, anchorRef]);

  /* Ferme sur Escape */
  useEffect(() => {
    const handle = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [onClose]);

  const Item = ({ icon: Icon, label, onClick, danger = false }) => (
    <button
      className={`${styles.optionItem} ${danger ? styles.optionDanger : ''}`}
      onClick={() => { onClick(); onClose(); }}
    >
      <Icon className={styles.optionIcon} />
      <span>{label}</span>
    </button>
  );

  return (
    <div className={styles.optionsDropdown} ref={modalRef} role="menu">

      {/* ── Actions communes ── */}
      <Item icon={FaLink}      label="Copier le lien"       onClick={onCopyLink} />
      {/**<Item icon={FaEyeSlash}  label="Masquer cette publication" onClick={() => {}} /> */}

      {/* ── Owner only ── */}
      {isOwner && (
        <>
          <div className={styles.optionDivider}/>
          <Item icon={FaTrash}  label="Supprimer"           onClick={() => onDelete(post._id)} danger />
        </>
      )}

      {/* ── Not owner ── */}
      {!isOwner && (
        <>
          <div className={styles.optionDivider}/>
          {/**<Item icon={FaUserTimes}  label="Ne plus suivre"     onClick={() => {}} />
          <Item icon={FaVolumeMute} label="Mettre en sourdine" onClick={() => {}} /> */}
          <Item icon={FaFlag}       label="Signaler"           onClick={() => onReport(post._id)} danger />
        </>
      )}
    </div>
  );
};

/* ─── PostHeader ──────────────────────────────────────────────── */
const PostHeader = ({ post, userId, setRefresh, refresh, navigate, onDelete, onReport }) => {
  const [open, setOpen]       = useState(false);
  const btnRef                = useRef(null);

  const handleCopyLink = useCallback(() => {
    const url = `${window.location.origin}/post/${post._id}`;
    navigator.clipboard.writeText(url);
  }, [post._id]);

  return (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        {post.userPP || post.pp ? (
          <img 
            src={post.userPP || post.pp} 
            alt={post.username} 
            className={styles.avatar}
            onClick={(e) => { e.stopPropagation(); navigate(`/profile/${post.userId}`); }}
          />
        ) : (
          <div 
            className={styles.avatarPlaceholder}
            onClick={(e) => { e.stopPropagation(); navigate(`/profile/${post.userId}`); }}
          >
            {post.username?.charAt(0).toUpperCase()}
          </div>
        )}
        <div className={styles.authorInfo}>
          <div className={styles.authorName}>
            <p onClick={(e) => { e.stopPropagation(); navigate(`/profile/${post.userId}`); }}>{post.username}</p>
            <div onClick={e => e.stopPropagation()}>
              <Friend
                userId={userId}
                authorId={post.userId}
                authorFollowers={post.followers}
                setRefresh={setRefresh}
                refresh={refresh}
              />
            </div>
            <span className={styles.postTime}>{manageTime(post.createdAt)}</span>
          </div>
        </div>
      </div>

      {/* Bouton 3 points */}
      <div className={styles.moreWrap}>
        <button
          ref={btnRef}
          className={`${styles.moreBtn} ${open ? styles.moreBtnActive : ''}`}
          onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
          aria-label="Options de la publication"
          aria-expanded={open}
          aria-haspopup="true"
        >
          <FaEllipsisV />
        </button>

        {open && (
          <PostOptionsModal
            post={post}
            userId={userId}
            onClose={() => setOpen(false)}
            onDelete={onDelete}
            onCopyLink={handleCopyLink}
            onReport={onReport}
            anchorRef={btnRef}
          />
        )}
      </div>
    </div>
  );
};

/* ─── Actions bar ─────────────────────────────────────────────── */
const Actions = ({ post, userId, onLike, onComment, onShare, onSave, saved, processing }) => (
  <div className={styles.mobileActions}>
    <div className={styles.leftActions}>
      <button
        className={`${styles.mobileActionBtn} ${post.likedByMe ? styles.liked : ''}`}
        onClick={() => onLike(post._id, post.userId, post.likedBy || [], post)}
        aria-label={post.likedByMe ? "Retirer la mention J'aime" : "Ajouter une mention J'aime"}
      >
        {post.likedByMe ? <FaHeart className={styles.iconLiked} /> : <FaRegHeart />}
        {(post.likesCount || post.likedBy?.length) > 0 && <span className={styles.count}>{post.likesCount || post.likedBy?.length}</span>}
      </button>

      <button 
        className={styles.mobileActionBtn} 
        onClick={() => onComment(post._id)}
        aria-label={`Afficher les ${post.postComment?.length || 0} commentaires`}
      >
        <FaRegCommentDots />
        {post.postComment?.length > 0 && <span className={styles.count}>{post.postComment.length}</span>}
      </button>

      <button className={styles.mobileActionBtn} onClick={() => onShare(post)} aria-label="Partager cette publication">
        <FaPaperPlane />
        {post.sharing?.length > 0 && <span className={styles.count}>{post.sharing.length}</span>}
      </button>
    </div>

    <button
      className={`${styles.mobileActionBtn} ${saved ? styles.saved : ''}`}
      onClick={() => onSave(post._id)}
      disabled={processing[`save-${post._id}`]}
      aria-label={saved ? "Retirer de vos favoris" : "Enregistrer dans vos favoris"}
    >
      {saved ? <FaBookmark /> : <FaRegBookmark />}
    </button>
  </div>
);

/* ─── Individual post wrapper ─────────────────────────────────── */
const PostWrap = ({ post, children, className }) => (
  <article className={className || styles.post}>
    <div className={styles.body}>{children}</div>
  </article>
);

const truncate = (text, maxLength = 200)=>{
  if (!text) return "";
  if (text.length <= maxLength) {
    return text;
  } return <>{text.slice(0, maxLength)}...<span className={styles.seemore}>Voir plus</span></>
}

/* ─── Main Posts component ────────────────────────────────────── */
const Posts = ({ setRefresh, refresh }) => {
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [textContent, setTextContent]       = useState('');
  const [isCommentLoading, setCommentLoad]  = useState(false);
  const [toast, setToast]                   = useState(null);
  const [savedPosts, setSavedPosts]         = useState([]);
  const [showComments, setShowComments]     = useState({});
  const [processing, setProcessing]         = useState({});
  const [optimisticLikes, setOptimisticLikes] = useState({});
  const commentInputRef = useRef(null);
  const navigate = useNavigate();

  const token  = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  const { data: posts = [], isLoading, isError, error, refetch } = usePosts(token);

  useEffect(() => { if (refresh) refetch(); }, [refresh, refetch]);
  useEffect(() => { if (isError) setToast(error?.response?.data?.message || 'Erreur de chargement'); }, [isError, error]);
  useEffect(() => { if (selectedPostId && commentInputRef.current) commentInputRef.current.focus(); }, [selectedPostId]);

  const setProc = (key, val) => setProcessing(p => ({ ...p, [key]: val }));

  /* ── Like ── */
  const handleLike = useCallback(async (postId, authorId, like, post) => {
    // Optimistic Update
    const isLiked = post.likedByMe || optimisticLikes[postId];
    setOptimisticLikes(prev => ({
      ...prev,
      [postId]: !isLiked
    }));

    try {
      await axios.put(`${API_URL}/post/like`, { postId, authorId }, { headers: { Authorization: `Bearer${token}` } });
      refetch();
      if (userId !== authorId && !isLiked)
        getNotif({ userId, authorId, message: "a aimé votre publication", type: 'like', post, token });
    } catch (e) { 
      // Rollback optimistic update
      setOptimisticLikes(prev => ({ ...prev, [postId]: isLiked }));
      setToast(e.response?.data?.message || 'Erreur'); 
    }
  }, [optimisticLikes, token, userId, refetch]);

  /* ── Comment ── */
  const handleComment = useCallback(async (postId, authorId) => {
    if (!textContent.trim() || isCommentLoading) return;
    setCommentLoad(true);
    try {
      const res = await axios.put(`${API_URL}/post/comment`,
        { commentary: textContent, currentPostId: postId, userId, authorId },
        { headers: { Authorization: `Bearer${token}` } }
      );
      refetch();
      setToast(res.data.message || 'Commentaire publié');
      if (userId !== authorId) getNotif({ userId, authorId, message: "a commenté votre publication", type: 'comment', token });
      setTextContent(''); setSelectedPostId(null);
    } catch (e) { setToast(e.response?.data?.message || 'Erreur'); }
    finally { setCommentLoad(false); }
  }, [textContent, isCommentLoading, token, userId, refetch]);

  /* ── Share ── */
  const handleShare = useCallback(async (post) => {
    const url = `${window.location.origin}/post/${post._id}`;
    if (navigator.share) {
      try { await navigator.share({ title: post.title || 'Publication', text: post.postText || '', img: post.postPicture || post.post.imageUrl || "", url }); }
      catch (e) { if (e.name !== 'AbortError') { navigator.clipboard.writeText(url); setToast('Lien copié !'); } return; }
    } else { navigator.clipboard.writeText(url); setToast('Lien copié !'); }
    try {
      await axios.put(`${API_URL}/post/sharing`, { currentPostId: post._id, userId },
        { headers: { Authorization: `Bearer ${token}` } });
      refetch();
    } catch (e) { console.error(e); }
  }, [token, userId, refetch]);

  /* ── Save ── */
  const handleSave = useCallback(async (postId) => {
    if (processing[`save-${postId}`]) return;
    setProc(`save-${postId}`, true);
    // Optimistic update
    setSavedPosts(prev =>
      prev.includes(postId) ? prev.filter(id => id !== postId) : [...prev, postId]
    );
    try {
      await axios.put(
        `${API_URL}/post/favoris`,
        { postId },                              // ✅ seul postId requis par la route
        { headers: { Authorization: `Bearer${token}` } }
      );
      refetch();
    } catch (e) {
      // Rollback si erreur
      setSavedPosts(prev =>
        prev.includes(postId) ? prev.filter(id => id !== postId) : [...prev, postId]
      );
      setToast('Erreur lors de l\'enregistrement');
    } finally {
      setProc(`save-${postId}`, false);
    }
  }, [processing, token, refetch]);

  /* ── Delete (owner) ── */
  const handleDelete = useCallback(async (postId) => {
    try {
      await axios.delete(`${API_URL}/post/${postId}`, { headers: { Authorization: `Bearer${token}` } });
      refetch();
      setToast('Publication supprimée');
    } catch (e) { setToast(e.response?.data?.message || 'Erreur lors de la suppression'); }
  }, [token, refetch]);

  /* ── Report ── */
  const handleReport = useCallback((postId) => {
    // Stocke le signalement — récupéré par le dashboard admin
    try {
      const existing = JSON.parse(localStorage.getItem('admin_reported') || '[]');
      const alreadyReported = existing.some(r => r.postId === postId?.toString());
      if (!alreadyReported) {
        existing.push({
          postId     : postId?.toString(),
          reportedAt : new Date().toISOString(),
          reportedBy : userId,
          reason     : 'Contenu inapproprié',
        });
        localStorage.setItem('admin_reported', JSON.stringify(existing));
      }
    } catch (e) { console.error(e); }
    setToast('Publication signalée');
  }, [userId]);

  const toggleComments = (id) =>{
    navigate(`/post/${id}`)
    //setShowComments(p => ({ ...p, [id]: !p[id] })), []
  };
    

  /* ── Props factories ── */
  const headerProps = (post) => ({
    post, userId, setRefresh, refresh, navigate,
    onDelete: handleDelete,
    onReport: handleReport,
  });

  const actionProps = (post) => {
    const isOptimistic = optimisticLikes[post._id];
    const isActuallyLiked = post.likedByMe;
    const finalLiked = isOptimistic !== undefined ? isOptimistic : isActuallyLiked;
    
    // Calcul du count optimiste
    let count = post.likesCount || post.likedBy?.length || 0;
    if (isOptimistic !== undefined && isOptimistic !== isActuallyLiked) {
      count = isOptimistic ? count + 1 : count - 1;
    }

    return {
      post: { ...post, likedByMe: finalLiked, likesCount: count }, 
      userId,
      onLike: handleLike, onComment: toggleComments,
      onShare: handleShare, onSave: handleSave,
      saved: post.favoris?.includes(userId) || savedPosts.includes(post._id?.toString()),
      processing,
    };
  };

  /* ─ Renderers ─ */
  const renderRegular = (post) => (
    <PostWrap key={post._id} post={post} className={post.postPicture ? styles.post : styles.textPost}>
      <PostHeader {...headerProps(post)} />
      {post.postText && 
        <div  onClick={() => navigate(`/post/${post._id}`)}>
          <p className={styles.postText}>{truncate(post.postText)}</p>
        </div>
      }
      {post.postPicture && (
        <div className={styles.imageWrapper} onClick={() => navigate(`/post/${post._id}`)}>
          <img src={post.postPicture} alt="post" loading="lazy" />
          {post.title && <div className={styles.articleOverlay}><h2>{truncate(post.title)}</h2></div>}
        </div>
      )}
      <Actions {...actionProps(post)} />
    </PostWrap>
  );

  const renderQuestion = (post) => {
    return (
      <PostWrap key={post._id} post={post}>
        <PostHeader {...headerProps(post)} />
        <div className={styles.badgeRow}>
          <span className={styles.typeBadge}>
            <FaQuestionCircle /> Débat
          </span>
        </div>
        <div onClick={() => navigate(`/post/${post._id}`)} className={styles.clickableBody}>
          <h3 className={styles.questionTitle}>{post.title}</h3>
          {post.postText && (
            <p className={styles.questionDetails}>{truncate(post.postText)}</p>
          )}

          <div className={styles.debateStats}>
            <div className={styles.statItem}>
              <FaUsers /> <span>{post.participantsCount || 0} participants</span>
            </div>
            <div className={styles.statItem}>
              <FaRegCommentDots /> <span>{post.argumentsCount || 0} arguments</span>
            </div>
          </div>


          {post.postPicture && (
            <div className={styles.imageWrapper}>
              <img src={post.postPicture} alt="post" loading="lazy" />
            </div>
          )}
          {post.tags?.length > 0 && (
            <div className={styles.tagsRow}>{post.tags.map((t, i) => <span key={i} className={styles.tag}>#{t}</span>)}</div>
          )}
        </div>
        

        <Actions {...actionProps(post)} />
      </PostWrap>
    );
  };

  const renderInfo = (post) => {
    const config = TYPE_CONFIG[post.type] || TYPE_CONFIG[post.postType] || { icon: FaNewspaper, color: '#f59e0b', label: 'Info' };
    const priority = PRIORITY_CONFIG[post.priority] || PRIORITY_CONFIG.normal;
    const Icon = config.icon;
    const d = post.post || post;
    return (
      <PostWrap key={post._id} post={post} className={d.priority === 'urgent' ? `${styles.post} ${styles.urgentPost}` : styles.post}>
        <PostHeader {...headerProps(post)} />
        <div className={styles.badgeRow}>
          <span className={styles.typeBadge} style={{ background: config.color + '18', color: config.color }}>
            <Icon /> {config.label}
          </span>
          {d.priority && d.priority !== 'normal' && (
            <span className={styles.priorityBadge} style={{ background: priority.color }}>{priority.label}</span>
          )}
        </div>
        <div  onClick={() => navigate(`/post/${post._id}`)}>
          <h3 className={styles.infoTitle}>{d.title}</h3>
          <p className={styles.infoBody}>{truncate(d.content || d.postText)}</p>
          {d.imageUrl && <div className={styles.imageWrapper}><img src={d.imageUrl} alt="Info" /></div>}
          {post.publicId && <CloudinaryImage publicId={post.publicId} width={600} height={400} />}
          {d.link && <a href={d.link} target="_blank" rel="noopener noreferrer" className={styles.linkButton}>🔗 En savoir plus</a>}
          
        </div>
        <Actions {...actionProps(post)} />
      </PostWrap>
    );
  };

  const renderEvent = (post) => {
    const d = post.post || {};
    return (
      <PostWrap key={post._id} post={post}>
        <PostHeader {...headerProps(post)} />
        <div className={styles.badgeRow}>
          <span className={styles.typeBadge} style={{ background: '#00ba7c18', color: '#00ba7c' }}>
            <FaCalendar /> Événement
          </span>
          {d.category && (
            <span className={styles.categoryBadge} style={{ background: CATEGORY_COLORS[d.category] + '18', color: CATEGORY_COLORS[d.category] }}>
              {d.category}
            </span>
          )}
        </div>
        <div  onClick={() => navigate(`/post/${post._id}`)}>
          <h3 className={styles.eventTitle}>{d.title}</h3>
          <p className={styles.eventDescription}>{truncate(d.description)}</p>
          {d.imageUri && <div className={styles.imageWrapper}><img src={d.imageUri} alt="Event" /></div>}
          <div className={styles.eventDetails}>
            {d.startDate && (
              <div className={styles.eventDetailRow}>
                <FaClock />
                <span>{fmtEvent(d.startDate)}{d.endDate && ` → ${fmtEvent(d.endDate)}`}</span>
              </div>
            )}
            {d.location && <div className={styles.eventDetailRow}><FaMapMarkerAlt /><span>{d.location}</span></div>}
            {d.maxParticipants && (
              <div className={styles.eventDetailRow}>
                <FaUsers /><span>{d.participants?.length || 0}/{d.maxParticipants} participants</span>
              </div>
            )}
          </div>
          <button className={`${styles.participateBtn} ${d.participants?.includes(userId) ? styles.participating : ''}`}>
            {/*d.participants?.includes(userId) ? <><FaCheckCircle /> Inscrit</> : <><FaPlusCircle /> Participer</>*/}
          </button>
        </div>
        
        <Actions {...actionProps(post)} />
      </PostWrap>
    );
  };

  const renderPost = (post) => {
    switch (post.postType) {
      case 'debate':
      case 'sale':     return renderQuestion(post);
      case 'event':    return renderEvent(post);
      case 'info':     return renderInfo(post);
      default:         return renderRegular(post);
    }

  };

  /* ─ States ─ */
  if (isLoading) return (
    <div className={styles.postsContainer}>
      <div className={styles.emptyState}><p>Chargement…</p></div>
    </div>
  );

  if (posts.length === 0) return (
    <div className={styles.postsContainer}>
      <div className={styles.emptyState}>
        <FaRegCommentDots size={48} />
        <h3>Aucune publication</h3>
        <p>Les publications de vos amis apparaîtront ici</p>
      </div>
    </div>
  );



  return (
    <div className={styles.postsContainer}>
      {toast && <Toast message={toast} setToast={setToast} />}
      {posts.map(renderPost)}
    </div>

  );

};




export default Posts;